import React from "react";

import dayjs from "dayjs";
import { ExternalLink } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ManualFacturaDebt } from "~/hooks/useNewDebtSummary";
import { formatCurrency } from "~/utils/helpers";

interface ManualFacturasTableProps {
  manualFacturas: ManualFacturaDebt[];
  cutoffDate?: Date;
}

export const ManualFacturasTable: React.FC<ManualFacturasTableProps> = ({
  manualFacturas,
  cutoffDate,
}) => {
  const getDaysInfo = (daysTo: number, cutoffDate?: Date) => {
    const isHistorical = !!cutoffDate;

    if (daysTo < 0) {
      const daysOverdue = Math.abs(daysTo);
      return (
        <span className="text-sm text-red-600">
          {isHistorical ? "Estaba " : ""}Vencido: {daysOverdue} días
        </span>
      );
    } else if (daysTo < 5) {
      const label =
        daysTo === 0
          ? isHistorical
            ? "Vencía hoy"
            : "Vence hoy"
          : `${isHistorical ? "Vencía en" : "Vence en"}: ${daysTo} días`;
      return <span className="text-sm text-orange-600">{label}</span>;
    } else {
      return (
        <span className="text-sm text-green-600">
          {isHistorical ? "Vencía en" : "Vence en"}: {daysTo} días
        </span>
      );
    }
  };

  if (manualFacturas.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center text-gray-500">
        No hay facturas manuales pendientes para este cliente
        {cutoffDate && (
          <span className="mt-1 block text-sm">en la fecha seleccionada</span>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border">
      <div className="border-b bg-gray-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">
            Facturas Manuales Pendientes ({manualFacturas.length}) - Total: $
            {formatCurrency(
              manualFacturas.reduce(
                (sum, mf) => sum + mf.debt_amount_at_cutoff,
                0
              )
            )}
          </h4>
          {manualFacturas.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(`/clients/${manualFacturas[0].client_id}`, "_blank")
              }
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Ver Cliente
            </Button>
          )}
        </div>
        {cutoffDate && (
          <p className="mt-1 text-xs text-gray-600">
            Deuda calculada al {dayjs(cutoffDate).format("DD/MM/YYYY")}
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Factura #</TableHead>
              <TableHead className="w-32">Fecha Emisión</TableHead>
              <TableHead className="w-32">Fecha Vencimiento</TableHead>
              <TableHead className="w-32 text-right">Monto Factura</TableHead>
              <TableHead className="w-32 text-right">Deuda Pendiente</TableHead>
              <TableHead className="w-40">Vencimiento</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {manualFacturas.map((factura, index) => (
              <TableRow
                key={factura.id}
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
              >
                <TableCell className="font-mono text-sm font-medium">
                  #{factura.doc_number}
                </TableCell>
                <TableCell className="text-sm">
                  {dayjs(factura.created_at).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="text-sm">
                  {dayjs(factura.payment_due_date).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="text-right font-medium">
                  ${formatCurrency(factura.original_balance)}
                </TableCell>
                <TableCell className="text-right font-medium text-red-600">
                  ${formatCurrency(factura.debt_amount_at_cutoff)}
                  {factura.payments_before_cutoff > 0 && (
                    <div className="text-xs text-green-600">
                      Pagado: ${formatCurrency(factura.payments_before_cutoff)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {getDaysInfo(factura.days_to_due_at_cutoff, cutoffDate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
